import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useNoticeBannersStackContext, useIsFocused } from '@sg/garnish';
import { NotificationCategoryType } from '@sg/shared-features-notifications';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTrackEventOnFocus } from '@order/Telemetry';

import { NotFoundView } from '../../components';
import { type NotificationCategory } from '../../hooks';
import { NotificationPreferences } from './components';
import { SUBSCRIPTION_TYPE } from './constants';
import { messages, useNotificationPreferences, useNotificationsContent } from './hooks';
import { NotificationPreferencesLoading } from './NotificationPreferencesLoading';

// ─────────────────────────────────────────────────────────────────────

export const NotificationPreferencesScreen = () => {
  const mappedContent = useNotificationsContent();

  const {
    preferences,
    isUpdating,
    permissions,
    isLoading,
    error,
    updateNotificationPreferences,
    refetch,
    unresolvedCategoriesNames,
  } = useNotificationPreferences({
    failureMessage: mappedContent.failedToUpdatePreferencesMessage,
  });

  const { push: addBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();

  const screenTitle = mappedContent.title;

  // ─── Flags ───────────────────────────────────────────────────────────

  const isOrderStatusEmailNotificationEnabled = useFeatureFlag(
    'CELS-3514-allow-order-status-email-preferences',
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const getCategoryTitle = useCallback(
    (category: NotificationCategory) => {
      const translationKey = category.id as NotificationCategoryType;

      return mappedContent.categories[translationKey]?.title ?? category.displayName;
    },
    [mappedContent.categories],
  );

  const getCategorySubtitle = useCallback(
    (categoryId: string) => {
      const subtitleKey = categoryId as NotificationCategoryType;

      return mappedContent.categories[subtitleKey]?.subtitle;
    },
    [mappedContent.categories],
  );

  const notifyAboutUnresolvedCategory = useCallback(() => {
    if (!unresolvedCategoriesNames) return;

    const message = mappedContent.unresolvedCategoryMessage?.replaceAll(
      '(category)',
      unresolvedCategoriesNames,
    );
    const fallbackMessage = formatMessage(messages.unresolvedCategoryMessage, {
      category: unresolvedCategoriesNames,
    });

    addBanner(
      {
        id: 'notifications.unresolved-category.banner',
        palette: 'neutral',
        text: message ?? fallbackMessage,
      },
      true,
    );
  }, [
    addBanner,
    formatMessage,
    mappedContent.unresolvedCategoryMessage,
    unresolvedCategoriesNames,
  ]);

  // ─── Effects ─────────────────────────────────────────────────────────

  const isScreenFocused = useIsFocused();

  useTrackEventOnFocus({ name: 'notification_preferences.viewed' }, { skip: !isScreenFocused });

  useEffect(notifyAboutUnresolvedCategory, [notifyAboutUnresolvedCategory]);

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <NotificationPreferencesLoading title={screenTitle} />;
  }

  if (error || !preferences?.length) {
    return (
      <NotificationPreferences.Container title={screenTitle}>
        <NotFoundView
          withoutFooter
          btnLabel={mappedContent.tryAgainButtonLabel}
          onBtnPress={refetch}
        />
      </NotificationPreferences.Container>
    );
  }

  // ─────────────────────────────────────────────────────────────────

  return (
    <NotificationPreferences.Container title={screenTitle}>
      {permissions.isGranted ? null : (
        <NotificationPreferences.Notice
          notice={mappedContent.pushNotificationsPermissionMessage}
          actionText={mappedContent.goToDeviceSettingsButtonLabel}
          onPress={permissions.openSettings}
        />
      )}

      {preferences.map((preference) => {
        const title = getCategoryTitle(preference);
        const subtitle = getCategorySubtitle(preference.id);

        const isOrderStatusOption = preference.id === NotificationCategoryType.OrderStatus;

        const shouldRenderOrderStatusEmailOption =
          !isOrderStatusOption || (isOrderStatusOption && isOrderStatusEmailNotificationEnabled);

        return (
          <NotificationPreferences.Content key={preference.id}>
            {title ? <NotificationPreferences.Title>{title}</NotificationPreferences.Title> : null}

            {subtitle ? (
              <NotificationPreferences.Subtitle>{subtitle}</NotificationPreferences.Subtitle>
            ) : null}

            <NotificationPreferences.Control.Container>
              <NotificationPreferences.Control.Section>
                <NotificationPreferences.Control.Title>
                  {mappedContent.pushNotificationOptionLabel}
                </NotificationPreferences.Control.Title>

                <NotificationPreferences.Control.Toggle
                  value={preference.pushSubscribe}
                  subscriptionType={SUBSCRIPTION_TYPE.PUSH}
                  category={preference.id}
                  onChange={updateNotificationPreferences}
                  isLoading={isUpdating}
                />
              </NotificationPreferences.Control.Section>

              {shouldRenderOrderStatusEmailOption ? (
                <NotificationPreferences.Control.Section>
                  <NotificationPreferences.Control.Title>
                    {mappedContent.emailOptionLabel}
                  </NotificationPreferences.Control.Title>

                  <NotificationPreferences.Control.Toggle
                    value={preference.emailSubscribe}
                    subscriptionType={SUBSCRIPTION_TYPE.EMAIL}
                    category={preference.id}
                    onChange={updateNotificationPreferences}
                    isLoading={isUpdating}
                  />
                </NotificationPreferences.Control.Section>
              ) : null}
            </NotificationPreferences.Control.Container>
          </NotificationPreferences.Content>
        );
      })}
    </NotificationPreferences.Container>
  );
};
